/**
 * TRACK 3 — Performance Diagnostics admin panel.
 *
 * Renders a compact table of viewer perf metrics with p50 / p95 / worst /
 * target / pass-fail. No backend dependency — pulls from the local
 * `perfTelemetry` buffer that the viewer fills as the operator works.
 */
import { useEffect, useState } from "react";
import { Activity, Check, X } from "lucide-react";
import { getPerfReport, clearPerf } from "@/lib/perfTelemetry";

const METRIC_LABEL = {
  sheet_open: "Sheet open (data load)",
  sheet_switch: "Sheet switch",
  first_render: "First render",
  tile_load: "Tile fetch",
  zoom: "Zoom step",
  pan: "Pan frame",
};

export function PerfDiagnosticsPanel() {
  const [report, setReport] = useState(getPerfReport());
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 2000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setReport(getPerfReport());
  }, [tick]);

  return (
    <div
      className="fop-surface-2 rounded-lg p-4 border border-[var(--fop-border)]"
      data-testid="perf-diagnostics-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-2">
          <Activity size={14} className="text-[var(--fop-primary)]" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--fop-text-2)]">
            Viewer performance
          </span>
        </div>
        <button
          type="button"
          onClick={() => { clearPerf(); setReport(getPerfReport()); }}
          className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
          data-testid="perf-clear-button"
        >
          Reset
        </button>
      </div>
      <table className="w-full text-[12px]" data-testid="perf-diagnostics-table">
        <thead>
          <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
            <th className="py-1 pr-2">Metric</th>
            <th className="py-1 pr-2">Samples</th>
            <th className="py-1 pr-2">p50</th>
            <th className="py-1 pr-2">p95</th>
            <th className="py-1 pr-2">Worst</th>
            <th className="py-1 pr-2">Target</th>
            <th className="py-1">Pass</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(report).map(([key, s]) => (
            <tr
              key={key}
              className="border-t border-[var(--fop-border)]"
              data-testid={`perf-row-${key}`}
            >
              <td className="py-1.5 pr-2 text-[var(--fop-text)]">{METRIC_LABEL[key] || key}</td>
              <td className="py-1.5 pr-2 font-mono text-[var(--fop-text-2)]">{s.n}</td>
              <td className="py-1.5 pr-2 font-mono">{s.n ? `${s.p50} ms` : "—"}</td>
              <td className="py-1.5 pr-2 font-mono">{s.n ? `${s.p95} ms` : "—"}</td>
              <td className="py-1.5 pr-2 font-mono">{s.n ? `${s.worst} ms` : "—"}</td>
              <td className="py-1.5 pr-2 font-mono text-[var(--fop-text-3)]">≤ {s.target_ms} ms</td>
              <td className="py-1.5">
                {s.n === 0 ? (
                  <span className="text-[var(--fop-text-3)] text-[11px]">no data</span>
                ) : s.pass ? (
                  <Check size={14} className="text-[var(--st-resolved,#10b981)]" />
                ) : (
                  <X size={14} className="text-[var(--st-escalated,#ef4444)]" />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-4)]">
        Samples are collected as you use the viewer. Open / pan / zoom a few sheets
        and the numbers populate. Local to this device.
      </div>
    </div>
  );
}

export default PerfDiagnosticsPanel;
